'use client';

import { sanitizeEmail } from '@/utils/sanitize-input';
import { containsXss } from '@/utils/validate-input';
import { Button } from '../../ui/button';
import { Input } from '../../ui/input';
import { PasswordField } from './PasswordField';

interface EmailPasswordLoginFormProps {
  email: string;
  password: string;
  errors: { email?: string; password?: string };
  errorMsg: string;
  roleError?: string;
  loading: boolean;
  onEmailChange: (email: string) => void;
  onPasswordChange: (password: string) => void;
  onLogin: () => void;
  onResetPasswordClick: () => void;
  onClearErrors?: () => void;
}

export function EmailPasswordLoginForm({
  email,
  password,
  errors,
  errorMsg,
  roleError,
  loading,
  onEmailChange,
  onPasswordChange,
  onLogin,
  onResetPasswordClick,
  onClearErrors,
}: EmailPasswordLoginFormProps) {
  const displayError = errorMsg || roleError;
  return (
    <div className="flex flex-col gap-4">
      <div className="flex flex-col gap-2">
        <label
          htmlFor="email"
          className="flex items-center justify-between cs-input-field"
        >
          Email
          <span className="cs-asterisk">*</span>
        </label>
        <Input
          id="email"
          type="email"
          placeholder=""
          value={email}
          onChange={(e) => {
            // Security: Sanitize email input to prevent XSS attacks
            // Allow partial email input while typing
            // Convert to lowercase for consistency
            const sanitized = sanitizeEmail(e.target.value.toLowerCase());
            // Block if contains malicious patterns
            if (!containsXss(sanitized)) {
              onEmailChange(sanitized);
            }
            onClearErrors?.();
          }}
          onFocus={() => onClearErrors?.()}
          onKeyUp={(e) => {
            if (e.key === 'Enter' || e.keyCode === 13) {
              onLogin();
            }
          }}
          className={`min-h-[40px] touch-manipulation cursor-pointer ${
            errors.email !== undefined ? 'border-destructive cs-input-ring-error' : ''
          }`}
        />
      </div>
      <div className="flex flex-col gap-2 relative">
        <label
          htmlFor="password"
          className="flex items-center justify-between cs-input-field"
        >
          Password
          <span className="cs-asterisk">*</span>
        </label>
        <PasswordField
          id="password"
          value={password}
          onChange={(value) => {
            // Security: Block XSS attempts in password field
            if (!containsXss(value)) {
              onPasswordChange(value);
            }
            onClearErrors?.();
          }}
          onFocus={() => onClearErrors?.()}
          onKeyUp={(e) => {
            if (e.key === 'Enter' || e.keyCode === 13) {
              onLogin();
            }
          }}
          hasError={errors.password !== undefined}
          className="cursor-pointer"
        />
      </div>
      
      <Button
        onClick={onLogin}
        variant="PrimaryFillButton"
        disabled={loading}
        className=" w-full touch-manipulation"
      >
        {loading ? (
          <span className="flex items-center justify-center gap-2">
            <div className="w-4 h-4 border-2 border-current border-t-transparent rounded-full animate-spin" />
            Logging in...
          </span>
        ) : (
          'Login'
        )}
      </Button>
      {displayError && (
          <h6 className="cs-error cs-h6 text-center">{displayError}</h6>
      )}
      <div className="text-center mt-4">
        <button
          onClick={onResetPasswordClick}
          className="cs-h6 cursor-pointer text-[var(--color-amethyst-light)] hover:text-[var(--color-amethyst-accent)] transition-colors"
        >
          Trouble logging in? Click here to reset password
        </button>
      </div>
    </div>
  );
}

